// Persists /app wizard state to sessionStorage so users who get redirected
// to Stripe Checkout (the most common upgrade path from the free tier)
// don't lose their work on return.
//
// Why sessionStorage (not localStorage):
//   - Tab-scoped; doesn't leak between separate browser windows / users
//     sharing a machine.
//   - Auto-cleared when the tab closes, which is the right hygiene for
//     a half-finished wizard state.
//   - Stripe Checkout opens in the SAME tab (top-level navigation), so
//     sessionStorage survives the round-trip just like the cookie store.
//
// What we save:
//   - Every primitive wizard field (step, postType, platform, names, ids,
//     quality, etc.).
//   - `productImages` — these are already base64 data URLs (StepUpload's
//     output), so they serialize trivially.
//   - `generatedImages` + `suggestedFrames` + `lastGenKey` — the OUTPUT
//     of /api/generate. CRITICAL to preserve: each generation costs the
//     user real credits, and a free-tier user who paid for an upgrade
//     and lands back on Step 7 must NOT be charged again. StepPreview's
//     cache check compares `lastGenKey` against the computed input key;
//     when restored together with matching inputs (productName, sceneId,
//     quality, etc.), the cache hits and no re-generation fires.
//   - `textOverlays` / `shapeOverlays` / `selectedPresetId` — Step 8
//     edits the user may have made before the upsell. Preserve so
//     they're not lost.
//
// What we DON'T save:
//   - `productFiles` (raw File objects). Files aren't JSON-serializable
//     and they're recreated from `productImages` data URLs on restore.
//
// Storage size — generated images can be ~200KB base64 each; a 4-frame
// carousel is ~800KB. Combined with the product image (~200KB) we're
// well under sessionStorage's ~5MB origin quota in normal browsers.
// If we ever blow the quota the try/catch in saveWizardSnapshot
// silently no-ops and the user pays the regeneration cost (worst-case
// fallback to pre-snapshot behavior).
//
// TTL: 30 minutes. Stripe Checkout sessions expire after 24h but a
// half-finished wizard older than 30 min is more likely abandoned than
// "in progress." Clear it to avoid surprising the user with stale state.

import type { Quality, Provider, SocialPostSlots } from './api';
import type { PostType } from '@/pages/app/components/StepCategory';
import type { SocialDesignId } from '@/pages/app/components/StepSocialDesign';
import type {
  TextOverlay,
  ShapeOverlay,
  SuggestedFrame,
} from '@/pages/app/lib/composeRender';

const STORAGE_KEY = 'sp_wizard_snapshot_v1';
const TTL_MS = 30 * 60 * 1000;  // 30 minutes

export interface WizardSnapshot {
  /** Wall-clock ms when the snapshot was written. Used to enforce TTL. */
  savedAt: number;
  step: number;
  postType: PostType | null;
  platform: string | null;
  /** base64 data URLs from StepUpload — convertible back to File on restore. */
  productImages: string[];
  /** Original filenames of the uploaded Files. Critical for cache-key
   *  stability — StepPreview's generation cache key includes
   *  `productFile.name`, so reconstructing a File with a synthetic name
   *  (e.g. `restored-1700000000.jpg`) on restore produces a different
   *  cache key than the original session and triggers an unwanted
   *  regeneration (= duplicate credit charge). Saving the names
   *  alongside the data URLs lets us rebuild Files with identical
   *  `.name` + `.size` so the cache hit holds. */
  productFileNames: string[];
  productName: string;
  productDescription: string;
  sceneId: string | null;
  quality: Quality;
  provider: Provider;
  socialDesignId: SocialDesignId | null;
  fashionModelId: string | null;
  garmentType: 'top' | 'bottoms' | 'shoes' | 'none' | null;
  garmentRestriction:
    | 'swimwear'
    | 'lingerie'
    | 'underwear'
    | 'sheer'
    | 'none'
    | null;
  hasHumanModel: boolean | null;
  productCategory: string | null;
  productTags: string[];
  /** Social Post route — preserved so users on Step 6 don't lose tweaks. */
  socialSlots: SocialPostSlots | null;
  /** Generated images (base64 data URLs). Saved so a user returning
   *  from checkout doesn't get charged a second time for the same
   *  generation. Pair with `lastGenKey` to make StepPreview's cache
   *  match on remount. */
  generatedImages: string[];
  /** AI-generated copy per frame. Matches `generatedImages` 1:1. */
  suggestedFrames: SuggestedFrame[];
  /** Cache key from the last successful generation. StepPreview compares
   *  this against a key it computes from current inputs; when equal, no
   *  regeneration fires. Setting this on restore is what prevents the
   *  duplicate credit charge. */
  lastGenKey: string | null;
  /** Step 8 layer state. */
  textOverlays: TextOverlay[];
  shapeOverlays: ShapeOverlay[];
  selectedPresetId: string | null;
}

export function saveWizardSnapshot(snap: Omit<WizardSnapshot, 'savedAt'>): void {
  try {
    const payload: WizardSnapshot = { ...snap, savedAt: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota exceeded / private-mode / disabled storage — silently no-op.
    // Worst case the user loses their wizard state on checkout return,
    // same as before this feature existed.
  }
}

export function loadWizardSnapshot(): WizardSnapshot | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as WizardSnapshot;
    if (!snap || typeof snap.savedAt !== 'number') return null;
    if (Date.now() - snap.savedAt > TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return snap;
  } catch {
    return null;
  }
}

export function clearWizardSnapshot(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Convert a base64 data URL back to a File so downstream API calls
 * (/api/generate, /api/analyze-product) can use it identically to a
 * fresh upload. Mirrors the helper inside StepUpload for URL-scraped
 * products; keep them in sync if the filename convention changes.
 */
export function dataUrlToFile(dataUrl: string, filename: string): File | null {
  try {
    const [meta, b64] = dataUrl.split(',');
    if (!b64) return null;
    const mimeMatch = meta.match(/data:([^;]+);base64/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  } catch {
    return null;
  }
}
